import React from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { Rocket, Target, BrainCircuit, Settings2, Maximize } from 'lucide-react';
interface SlideLoopProps {
  onContinue: () => void;
}
export function SlideLoop({
  onContinue
}: SlideLoopProps) {
  const steps = [{
    id: '01',
    title: 'Launch',
    icon: <Rocket className="w-5 h-5" data-id="element-351" />,
    desc: 'Campaign goes live across selected countries and channels'
  }, {
    id: '02',
    title: 'Capture',
    icon: <Target className="w-5 h-5" data-id="element-352" />,
    desc: 'HCPs answer quizzes, polls, CTAs and content choices'
  }, {
    id: '03',
    title: 'Interpret',
    icon: <BrainCircuit className="w-5 h-5" data-id="element-353" />,
    desc: 'We identify knowledge gaps, message preference and country differences'
  }, {
    id: '04',
    title: 'Optimize',
    icon: <Settings2 className="w-5 h-5" data-id="element-354" />,
    desc: 'Next campaign adjusts wording, content path, CTA and channel focus'
  }, {
    id: '05',
    title: 'Scale',
    icon: <Maximize className="w-5 h-5" data-id="element-355" />,
    desc: 'Winning patterns are reused across brands and markets'
  }];
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-14 max-w-7xl mx-auto" data-id="element-356">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} data-id="element-357">
        <span className="text-teal-400 text-xs uppercase tracking-widest font-medium" data-id="element-358">
          The Snakker Flywheel
        </span>
        <h2 className="text-3xl md:text-5xl font-serif text-white mt-3 mb-3" data-id="element-359">
          Every interaction becomes a{' '}
          <span className="text-teal-400" data-id="element-360">decision signal.</span>
        </h2>
        <p className="text-base text-white/60 max-w-3xl" data-id="element-361">
          Snakker does not only report what happened. We use the data to decide
          what to improve next.
        </p>
      </motion.div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-12 mt-12 items-center" data-id="element-362">
        {/* LEFT — Circular Flywheel */}
        <div className="lg:col-span-7 relative flex items-center justify-center min-h-[400px]" data-id="element-363">
          <div className="absolute w-64 h-64 rounded-full border border-teal-400/20 border-dashed animate-[spin_40s_linear_infinite]" data-id="element-364" />
          <div className="absolute w-48 h-48 rounded-full border border-teal-400/10 animate-[spin_30s_linear_infinite_reverse]" data-id="element-365" />

          <div className="absolute w-24 h-24 rounded-full bg-navy-800 border border-teal-400/40 flex items-center justify-center z-10 shadow-[0_0_30px_rgba(45,212,191,0.15)]" data-id="element-366">
            <div className="text-center" data-id="element-367">
              <div className="text-teal-400 font-serif text-lg" data-id="element-368">Snakker</div>
              <div className="text-white/40 text-[9px] uppercase tracking-widest mt-0.5" data-id="element-369">
                Loop
              </div>
            </div>
          </div>

          {steps.map((step, i) => {
          const angle = i * 360 / steps.length - 90;
          const rad = angle * Math.PI / 180;
          const radius = 140;
          const x = Math.cos(rad) * radius;
          const y = Math.sin(rad) * radius;
          return <motion.div key={step.id} initial={{
            opacity: 0,
            scale: 0
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: i * 0.15 + 0.3,
            type: 'spring'
          }} className="absolute flex flex-col items-center w-32 text-center" style={{
            transform: `translate(${x}px, ${y}px)`
          }} data-id="element-370">
                <div className="w-12 h-12 rounded-xl bg-navy-700 border border-teal-400/30 flex items-center justify-center text-teal-400 mb-2 shadow-lg" data-id="element-371">
                  {step.icon}
                </div>
                <div className="text-white font-medium text-sm mb-1" data-id="element-372">
                  {step.title}
                </div>
                <div className="text-white/50 text-[10px] leading-tight" data-id="element-373">
                  {step.desc}
                </div>
              </motion.div>;
        })}
        </div>

        {/* RIGHT — Proof Card */}
        <div className="lg:col-span-5 space-y-6" data-id="element-374">
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.8
        }} className="bg-navy-800/60 border border-teal-400/30 rounded-2xl p-6" data-id="element-375">
            <p className="text-white/40 text-[10px] uppercase tracking-widest font-medium mb-4" data-id="element-376">
              What Arnaud gets back
            </p>
            <ul className="space-y-4" data-id="element-377">
              {['Country-by-country engagement patterns', 'Message resonance by brand', 'HCP knowledge gaps', 'CTA performance', 'Recommendations for the next campaign'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-378">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal-400 mt-1.5 flex-shrink-0" data-id="element-379" />
                  <span className="text-white/80 text-sm" data-id="element-380">{item}</span>
                </li>)}
            </ul>
          </motion.div>

          <div className="border-l-2 border-teal-400/40 pl-4 py-1" data-id="element-381">
            <p className="text-white/70 text-sm italic" data-id="element-382">
              "The report is not the end. It is the brief for the next
              campaign."
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-end mt-4" data-id="element-383">
        <Button onClick={onContinue} data-id="element-384">See the proof →</Button>
      </div>
    </div>;
}